
import React, { useMemo } from 'react';
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PaginationNumbersProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const PaginationNumbers = React.memo(({
  currentPage,
  totalPages,
  onPageChange
}: PaginationNumbersProps) => {
  const pages = useMemo(() => {
    const result: (number | string)[] = [];
    
    if (currentPage > 2) {
      result.push(0);
      if (currentPage > 3) result.push('...');
    }

    for (let i = Math.max(0, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!result.includes(i)) result.push(i);
    }

    if (currentPage < totalPages - 3) {
      if (currentPage < totalPages - 4) result.push('...');
      if (!result.includes(totalPages - 1)) result.push(totalPages - 1);
    }

    return result;
  }, [currentPage, totalPages]);

  return (
    <div 
      className="flex items-center gap-2"
      role="group"
      aria-label="Sélection de page"
    >
      {pages.map((page, index) => 
        page === '...' ? (
          <span 
            key={`ellipsis-${index}`} 
            className="px-2 flex items-center"
            aria-hidden="true"
          >
            ...
          </span>
        ) : (
          <Tooltip key={`page-${page}`}>
            <TooltipTrigger asChild>
              <Button
                variant={currentPage === page ? "default" : "outline"}
                onClick={() => onPageChange(page as number)}
                className={`w-8 h-8 sm:w-10 sm:h-10 p-0 transition-all duration-200
                  ${currentPage === page ? 'animate-scale-in' : 'hover:scale-105'}`}
                aria-label={`Page ${(page as number) + 1}`}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {(page as number) + 1}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Aller à la page {(page as number) + 1}</p>
            </TooltipContent>
          </Tooltip>
        )
      )}
    </div>
  );
});

PaginationNumbers.displayName = 'PaginationNumbers';
